import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  PLATFORMS, fetchVideos, fetchVideo, addVideo, reportVideo, refreshVideoThumbnail, guessPlatform,
  deleteVideo, moderateVideo,
} from '../../utils/videos';
import { friendlyCallError } from '../../utils/reviews';
import { ReportButton } from '../reviews/bits';
import AuthModal from '../AuthModal';
import { grabVideoFrame } from '../../utils/videoFrame';
import { uploadRestaurantImage, friendlyUploadError } from '../../utils/uploadImage';
import { storage, auth } from '../../firebase';
import { useConfirm } from '../ConfirmDialog';
import { META_VIDEOS_ENABLED } from '../../content/features';
import './VideoShelf.css';

// TikToks, Reels and Shorts about this restaurant, as a swipeable shelf of
// covers. Nothing from the platforms loads until someone taps a cover — a
// page with a dozen live TikTok players would be unusably slow on a phone —
// and then only that one player, in a dialog.
export default function VideoShelf({ restaurant }) {
  const { user, isGuest, isAdmin, currentUid, ensureGuest, refreshGuestProfile, openClaim } = useAuth();
  const [videos, setVideos] = useState(null);
  const [adding, setAdding] = useState(false);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(null);
  const [signIn, setSignIn] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  // A frame taken from the clip on this device, for the platforms that
  // hand out no cover of their own.
  const [cover, setCover] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  const inputRef = useRef(null);
  const shelfRef = useRef(null);
  // Which arrows to show: none at all until the row is wider than the
  // space it has (a phone swipes, and a short row needs no arrows).
  const [arrows, setArrows] = useState({ left: false, right: false });

  useEffect(() => {
    let live = true;
    fetchVideos(restaurant.id)
      .then((list) => { if (live) setVideos(list); })
      .catch(() => { if (live) setVideos([]); });
    return () => { live = false; };
  }, [restaurant.id]);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  // Adding needs an account. A guest already has posts tied to their uid, so
  // they get the claim flow that keeps them; everyone else gets sign-up.
  function startAdding() {
    setError('');
    if (!user) {
      if (isGuest) openClaim();
      else setSignIn(true);
      return;
    }
    setAdding((v) => !v);
  }

  async function pickCover(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setCoverBusy(true);
    setError('');
    try {
      const frame = await grabVideoFrame(file);
      setCover((prev) => {
        if (prev) URL.revokeObjectURL(prev.preview);
        return { file: frame, preview: URL.createObjectURL(frame) };
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setCoverBusy(false);
    }
  }

  function dropCover() {
    setCover((prev) => {
      if (prev) URL.revokeObjectURL(prev.preview);
      return null;
    });
  }

  async function submit(e) {
    e.preventDefault();
    if (busy || !link.trim()) return;
    setBusy(true);
    setError('');
    const asGuest = !user;
    try {
      await ensureGuest();
      // Uploaded first so the callable only ever receives a URL it can
      // check against this account's own folder.
      const coverUrl = cover ? await uploadRestaurantImage(storage, auth, cover.file) : null;
      const res = await addVideo({ businessId: restaurant.id, url: link.trim(), coverUrl });
      const video = await fetchVideo(restaurant.id, res.id);
      if (video) setVideos((prev) => [video, ...(prev || []).filter((v) => v.id !== video.id)]);
      setLink('');
      dropCover();
      setAdding(false);
      if (asGuest) refreshGuestProfile();
    } catch (err) {
      setError(err?.isUploadError ? friendlyUploadError(err) : friendlyCallError(err));
    } finally {
      setBusy(false);
    }
  }

  // A TikTok cover URL expires after a while; ask the server for a fresh one
  // once, and fall back to the placeholder if the video is gone.
  async function coverFailed(video) {
    if (video.platform !== 'tiktok' || video.refreshTried) {
      setVideos((prev) => prev.map((v) => (v.id === video.id ? { ...v, thumbnailUrl: null } : v)));
      return;
    }
    setVideos((prev) => prev.map((v) => (v.id === video.id ? { ...v, refreshTried: true } : v)));
    try {
      const res = await refreshVideoThumbnail({ businessId: restaurant.id, videoId: video.id });
      setVideos((prev) => prev.map((v) => (v.id === video.id ? { ...v, thumbnailUrl: res.thumbnailUrl || null } : v)));
    } catch {
      setVideos((prev) => prev.map((v) => (v.id === video.id ? { ...v, thumbnailUrl: null } : v)));
    }
  }

  // Their own video goes for good; an admin hides someone else's, which is
  // what the Videos section of the admin panel does, so the record survives.
  async function removeVideo(video) {
    const mine = !!user && video.addedBy === currentUid;
    const ok = await confirm(mine
      ? {
        title: 'Remove your video?',
        body: `It comes off ${restaurant.name}'s page for good. The original stays on your account wherever you posted it.`,
        confirmLabel: 'Remove',
        cancelLabel: 'Keep it',
      }
      : {
        title: 'Hide this video?',
        body: 'It comes off the page straight away and stays in the admin Videos list, so you can put it back.',
        confirmLabel: 'Hide video',
      });
    if (!ok) return;
    setError('');
    try {
      if (mine) await deleteVideo({ businessId: restaurant.id, videoId: video.id });
      else await moderateVideo({ businessId: restaurant.id, videoId: video.id, action: 'hide' });
      setVideos((prev) => (prev || []).filter((v) => v.id !== video.id));
    } catch (err) {
      setError(friendlyCallError(err));
    }
  }

  const mineVideo = (video) => !!user && video.addedBy === currentUid;
  const canRemove = (video) => mineVideo(video) || isAdmin;

  // A card plus its gap, so one press lands the next cover at the edge
  // instead of part-way across it.
  const step = () => {
    const shelf = shelfRef.current;
    const card = shelf?.querySelector('.video-card-wrap');
    return card ? card.getBoundingClientRect().width + 10 : 160;
  };

  const slide = (direction) => {
    // scroll-behavior: smooth on the shelf carries the animation.
    shelfRef.current?.scrollBy({ left: direction * step(), behavior: 'smooth' });
  };

  const syncArrows = useCallback(() => {
    const shelf = shelfRef.current;
    if (!shelf) return;
    const max = shelf.scrollWidth - shelf.clientWidth;
    setArrows(max < 8
      ? { left: false, right: false }
      : { left: shelf.scrollLeft > 4, right: shelf.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    syncArrows();
    const shelf = shelfRef.current;
    if (!shelf) return undefined;
    const observer = new ResizeObserver(syncArrows);
    observer.observe(shelf);
    return () => observer.disconnect();
  }, [videos, syncArrows]);

  const guess = guessPlatform(link);
  const offPlatform = !META_VIDEOS_ENABLED && (guess === 'instagram' || guess === 'facebook');
  // Meta removed thumbnail_url from oEmbed, so these two never arrive
  // with a cover; TikTok and YouTube always do. Only reachable while
  // META_VIDEOS_ENABLED is on.
  const needsCover = !offPlatform && (guess === 'instagram' || guess === 'facebook');
  const count = videos?.length || 0;

  return (
    <section className="videos" aria-labelledby="videos-heading">
      <div className="videos__head">
        <h2 id="videos-heading" className="videos__title">Videos</h2>
        {count > 0 && <span className="videos__count">{count}</span>}
        <button type="button" className="videos__add" onClick={startAdding} aria-expanded={adding}>
          {adding ? 'Cancel' : '+ Add Video'}
        </button>
      </div>

      {adding && (
        <form className="videos__form" onSubmit={submit}>
          <input
            ref={inputRef}
            className="videos__input"
            type="url"
            inputMode="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Paste a TikTok link"
            aria-label="Video link"
            disabled={busy}
          />
          <button type="submit" className="videos__submit" disabled={busy || !link.trim() || offPlatform}>
            {busy ? 'Adding…' : 'Add'}
          </button>
          <p className={`videos__hint${offPlatform ? ' videos__hint--off' : ''}`}>
            {offPlatform
              ? `${PLATFORMS[guess].name} videos aren’t supported — paste a TikTok`
              : guess
                ? `${PLATFORMS[guess].name} link`
                : META_VIDEOS_ENABLED
                  ? 'TikTok · Instagram Reels · Facebook Reels · YouTube Shorts'
                  : 'TikTok · YouTube Shorts'}
          </p>
          {needsCover && (
            <div className="videos__cover">
              {cover ? (
                <>
                  <img className="videos__cover-preview" src={cover.preview} alt="The cover taken from your clip" />
                  <span className="videos__cover-text">Cover taken from your clip.</span>
                  <button type="button" className="videos__cover-drop" onClick={dropCover} disabled={busy}>Remove</button>
                </>
              ) : (
                <>
                  <label className="videos__cover-pick">
                    <input type="file" accept="video/*" onChange={pickCover} disabled={busy || coverBusy} />
                    {coverBusy ? 'Reading the clip…' : 'Add a cover'}
                  </label>
                  <span className="videos__cover-text">
                    {PLATFORMS[guess].name} doesn’t give us a cover image. Got the clip on this device? Pick it and
                    we’ll use its first frame — the video itself stays on your phone.
                  </span>
                </>
              )}
            </div>
          )}
          {error && <p className="videos__error" role="alert">{error}</p>}
        </form>
      )}

      {!adding && error && <p className="videos__error videos__error--standalone" role="alert">{error}</p>}

      {videos === null ? (
        <div className="videos__shelf" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="video-card video-card--skeleton" />)}
        </div>
      ) : count === 0 ? (
        !adding && (
          <button type="button" className="videos__empty" onClick={startAdding}>
            <strong>No videos yet.</strong> Seen {restaurant.name} on TikTok? Paste the link.
          </button>
        )
      ) : (
        <div className="videos__rail">
          {arrows.left && (
            <button type="button" className="videos__arrow videos__arrow--left" onClick={() => slide(-1)} aria-label="Previous videos">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {arrows.right && (
            <button type="button" className="videos__arrow videos__arrow--right" onClick={() => slide(1)} aria-label="More videos">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        <div className="videos__shelf" ref={shelfRef} onScroll={syncArrows}>
          {videos.map((v) => (
            <VideoCard
              key={v.id}
              video={v}
              restaurant={restaurant}
              onPlay={() => setPlaying(v)}
              onCoverError={() => coverFailed(v)}
              onRemove={canRemove(v) ? () => removeVideo(v) : null}
              removeLabel={mineVideo(v) ? 'Remove your video' : 'Hide this video'}
            />
          ))}
        </div>
        </div>
      )}

      {playing && (
        <VideoPlayer
          video={playing}
          restaurant={restaurant}
          ensureGuest={ensureGuest}
          onClose={() => setPlaying(null)}
        />
      )}

      {signIn && <AuthModal defaultTab="signup" onClose={() => setSignIn(false)} />}
      {confirmDialog}
    </section>
  );
}

// For YouTube, try the vertical Shorts cover first (it only exists for
// Shorts), then the standard one — which is letterboxed, so it gets cropped.
// A missing Shorts cover doesn't error: YouTube serves a 120×90 grey
// placeholder instead, so that exact size also means "fall back".
function useCover(video, onCoverError) {
  const isYouTube = video.platform === 'youtube' && video.videoId;
  const [stage, setStage] = useState(isYouTube ? 'short' : 'stored');
  if (isYouTube && stage === 'short') {
    return {
      src: `https://i.ytimg.com/vi/${video.videoId}/oardefault.jpg`,
      letterboxed: false,
      onError: () => setStage('stored'),
      onLoad: (e) => {
        if (e.currentTarget.naturalWidth === 120 && e.currentTarget.naturalHeight === 90) setStage('stored');
      },
    };
  }
  return { src: video.thumbnailUrl, letterboxed: !!isYouTube, onError: onCoverError, onLoad: undefined };
}

function VideoCard({ video, restaurant, onPlay, onCoverError, onRemove, removeLabel }) {
  const platform = PLATFORMS[video.platform] || { name: video.platform };
  const cover = useCover(video, onCoverError);
  const label = `Play ${platform.name} video${video.authorName ? ` by ${video.authorName}` : ''}`;
  return (
    <div className="video-card-wrap">
      {onRemove && (
        <button type="button" className="video-card__remove" onClick={onRemove} aria-label={removeLabel} title={removeLabel}>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path d="M3 3l6 6M9 3l-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      )}
      <button type="button" className={`video-card video-card--${video.platform}`} onClick={onPlay} aria-label={label}>
      {cover.src ? (
        <img
          className={`video-card__img${cover.letterboxed ? ' video-card__img--letterboxed' : ''}`}
          src={cover.src}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={cover.onError}
          onLoad={cover.onLoad}
        />
      ) : (
        // No cover to show (Instagram/Facebook never provide one): a designed
        // placeholder from the restaurant's own photo and the platform.
        <span className="video-card__placeholder" aria-hidden="true">
          {restaurant.imageUrl && <img src={restaurant.imageUrl} alt="" className="video-card__placeholder-img" />}
          <span className="video-card__placeholder-name">{platform.name}</span>
        </span>
      )}
      <span className="video-card__platform">{platform.name}</span>
      <span className="video-card__play" aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5z" fill="currentColor" /></svg>
      </span>
      <span className="video-card__meta">
        {video.authorName && <span className="video-card__author">{video.authorName}</span>}
        {video.addedByRestaurant && <span className="video-card__by">Posted by the venue</span>}
      </span>
      </button>
    </div>
  );
}

function VideoPlayer({ video, restaurant, ensureGuest, onClose }) {
  const platform = PLATFORMS[video.platform] || { name: video.platform };
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const overlayRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      className="video-player"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${platform.name} video`}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="video-player__box">
        <button ref={closeRef} type="button" className="video-player__close" onClick={onClose} aria-label="Close video">×</button>
        <div className={`video-player__frame video-player__frame--${video.platform}`}>
          {/* embedUrl is built server-side from the video id (functions/lib/videoUrls.js),
              so this can only ever be the platform's own player. */}
          {!playerLoaded && video.thumbnailUrl && <img className="video-player__poster" src={video.thumbnailUrl} alt="" />}
          <iframe
            onLoad={() => setPlayerLoaded(true)}
            src={video.embedUrl}
            title={video.title || `${platform.name} video`}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-presentation"
          />
        </div>
        <div className="video-player__info">
          {video.title && <p className="video-player__title">{video.title}</p>}
          <p className="video-player__byline">
            {video.authorName && <>{video.authorName} on {platform.name}</>}
            {video.authorName && ' · '}
            Added by {video.addedByHandle}
            {video.addedByRestaurant ? ' (the venue)' : ''}
          </p>
          <div className="video-player__actions">
            <a href={video.canonicalUrl} target="_blank" rel="noopener noreferrer nofollow" className="video-player__open">
              Open on {platform.name} ↗
            </a>
            <ReportButton
              businessId={restaurant.id}
              ensureGuest={ensureGuest}
              send={(reason) => reportVideo({ businessId: restaurant.id, videoId: video.id, reason })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
